import { omit } from "radash"

import type { PackageState } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-types"
import { QUERY_STRING_PROPS } from "~config/constants"
import { useQueryParamObject } from "~utils/use-query-param"
import { isUndefined } from "~utils/helpers"

export const DEFAULT_CHECKOUT_QUERY_PARAMS = {
    [QUERY_STRING_PROPS.PACKAGE_ID]: null,
    [QUERY_STRING_PROPS.BOOKING_INVITE_ID]: null,
    [QUERY_STRING_PROPS.QUOTE_ID]: null,
    [QUERY_STRING_PROPS.BOOKING_ID]: null,
    [QUERY_STRING_PROPS.STEP]: null,
    [QUERY_STRING_PROPS.QUOTE_PACKAGE_ID]: null,
    [QUERY_STRING_PROPS.CHECKOUT_STATE]: null,
}

export interface CheckoutQueryParams {
    package_id: string | null
    booking_invite_id: string | null
    quote_id: string | null
    booking_id: string | null
    step: string | null
    quote_package_id: string | null
    checkout_state: CheckoutState | null
}

export interface CheckoutState {
    dates: number[]
    packageState: PackageState
}

// This "raw" type is used to define the query params for useQueryParamObject.
// This is before parsing any object props stored as strings in the URL.
interface CheckoutQueryParamsRaw extends Record<string, string | null> {
    package_id: string | null
    booking_invite_id: string | null
    quote_id: string | null
    booking_id: string | null
    step: string | null
    quote_package_id: string | null
    checkout_state: string | null
}

export type UpdateCheckoutQueryParamsFunc = (
    update: Partial<CheckoutQueryParams>
) => void

export default function useCheckoutQueryParams() {
    const [checkoutQueryParamsRaw, updateCheckoutQueryParamsRaw] =
        useQueryParamObject<CheckoutQueryParamsRaw>(
            DEFAULT_CHECKOUT_QUERY_PARAMS
        )
    const checkoutState = parseObjectParamSafely<CheckoutState>(
        checkoutQueryParamsRaw.checkout_state
    )
    const checkoutQueryParams = {
        ...checkoutQueryParamsRaw,
        [QUERY_STRING_PROPS.CHECKOUT_STATE]: checkoutState,
    }

    const updateCheckoutQueryParams = (
        update: Partial<CheckoutQueryParams>
    ) => {
        const newCheckoutState = update.checkout_state
            ? JSON.stringify(update.checkout_state)
            : null

        const formattedUpdate = isUndefined(update.checkout_state)
            ? omit(update, ["checkout_state"])
            : { ...update, checkout_state: newCheckoutState }

        updateCheckoutQueryParamsRaw(formattedUpdate)
    }

    return { checkoutQueryParams, updateCheckoutQueryParams }
}

function parseObjectParamSafely<ResultType>(
    param: string | null
): ResultType | null {
    if (!param) return null

    try {
        return JSON.parse(param)
    } catch (error) {
        return null
    }
}
